/**
 * HTD Home — ผลรางวัลแบบใหม่ (glass cards + section tabs)
 * ใช้ useLotteryResults ชุดเดียวกับ ModernLotteryResultSections
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useLotteryResults } from '../../hooks/useLotteryResults'
import {
  getWinningNumbers,
  getGovernmentThreeDownList,
  hasAnyResult,
  displayValue,
  getRoundDrawLabel,
  isRoundCancelled,
} from '../../utils/resultDisplayUtils'
import { getRoundImage, canRoundAcceptBet, isRoundNotYetOpen, type LotteryRound } from '../../services/lotteryService'
import './HtdLotteryResults.css'

function isYeekeeLikeRound(round: LotteryRound | null | undefined): boolean {
  if (!round) return false
  const s = (round.huayTypeName || '') + (round.huayName || '') + (round.roundNumber || '')
  return /ยี่กี|ยี|yeekee/i.test(s)
}

function getWipThreshold(
  round: LotteryRound | null | undefined,
  wipYeekee: number,
  wipOther: number
): number {
  return isYeekeeLikeRound(round) ? wipYeekee : wipOther
}

/** ใกล้ปิดรับ / ประกาศผล — ตรง lobby-huay */
function isRoundWipWup(
  round: LotteryRound | null | undefined,
  thresholdMinutes: number,
  now: number
): boolean {
  if (!round || isRoundCancelled(round)) return false
  const notYetOpen = isRoundNotYetOpen(round)
  const active = canRoundAcceptBet(round) && !notYetOpen
  if (!active) return false
  const minutesRemaining = (new Date(round.betEndTime).getTime() - now) / (60 * 1000)
  return minutesRemaining <= thresholdMinutes && minutesRemaining > 0
}

function todayBangkokYYYYMMDD(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

function formatYeekeeRoundLabel(roundNumber: string | undefined): string {
  if (!roundNumber) return ''
  const parts = roundNumber.split('-')
  const suffix = parts[parts.length - 1]
  if (/^\d+$/.test(suffix)) return String(parseInt(suffix, 10))
  return roundNumber
}

function getYeekeeRoundTimeLabel(round: LotteryRound | null): string {
  if (!round?.betEndTime) return 'รอบ -'
  const t = new Date(round.betEndTime).toLocaleTimeString('th-TH', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit',
    minute: '2-digit',
  })
  return `รอบ ${t} น.`
}

function getRoundImageSrc(
  round: LotteryRound | null,
  getAssetUrl: (path: string) => string,
  fallbackPath: string
): string {
  const urlOrPath = round ? getRoundImage(round) : fallbackPath
  if (typeof urlOrPath === 'string' && /^https?:\/\//i.test(urlOrPath)) return urlOrPath
  return getAssetUrl(urlOrPath || fallbackPath)
}

type TabId =
  | 'all'
  | 'gov'
  | 'set'
  | 'thai_stock'
  | 'foreign'
  | 'stock'
  | 'vip'
  | 'visa'
  | 'yeekee_vip'

const MAX_CARDS = 24

export interface HtdLotteryResultsProps {
  tenantId?: string
  getAssetUrl: (path: string) => string
  handleImgError: (e: React.SyntheticEvent<HTMLImageElement>) => void
  showPoolError?: boolean
  wipWupMinutes?: number
  wipWupMinutesOther?: number
  /** Controlled date (YYYY-MM-DD Bangkok). Defaults to today. */
  selectedDate?: string
  onSelectedDateChange?: (date: string) => void
  showDatePicker?: boolean
  targetRoundId?: string | null
  onYeekeeRoundClick?: (round: LotteryRound) => void
  className?: string
}

function DigitTile({
  label,
  value,
  size = 'md',
  waiting,
  wip,
}: {
  label: string
  value: string
  size?: 'sm' | 'md' | 'lg'
  waiting?: boolean
  wip?: boolean
}) {
  const isPending = waiting || value === 'รอผล' || value === 'ยกเลิก'
  const chars = isPending || /[,，]/.test(value) ? null : Array.from(value)

  return (
    <div
      className={`htdr-digit htdr-digit--${size}${isPending ? ' is-pending' : ' is-ready'}${wip && isPending ? ' is-wip' : ''}`}
    >
      <span className="htdr-digit__label">{label}</span>
      {chars ? (
        <span className="htdr-digit__value" aria-label={value}>
          {chars.map((ch, i) => (
            <span
              key={`${ch}-${i}`}
              className="htdr-digit__ch"
              style={{ ['--ch-i' as string]: i }}
            >
              {ch}
            </span>
          ))}
        </span>
      ) : (
        <span className={`htdr-digit__value${wip ? ' wip_wup' : ''}`}>{value}</span>
      )}
    </div>
  )
}

function GovBottomThreeBlock({
  numbers,
  isCancelled,
  hasResult,
  wip,
}: {
  numbers: string[]
  isCancelled: boolean
  hasResult: boolean
  wip?: boolean
}) {
  const pendingText = isCancelled ? 'ยกเลิก' : hasResult && numbers.length > 0 ? '' : 'รอผล'
  const isPending = pendingText !== ''

  return (
    <div className={`htdr-gov-bottom3${wip && isPending ? ' is-wip' : ''}`}>
      <span className="htdr-gov-bottom3__label">3 ตัวล่าง</span>
      {isPending ? (
        <span className={`htdr-gov-bottom3__pending${wip ? ' wip_wup' : ''}`}>{pendingText}</span>
      ) : (
        <div className="htdr-gov-bottom3__grid" aria-label={`3 ตัวล่าง ${numbers.join(', ')}`}>
          {numbers.map((num, i) => (
            <span key={`${num}-${i}`} className="htdr-gov-bottom3__chip">
              {num}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function getYeekeeResultDigits(round: LotteryRound | null): { threeUp: string; twoDown: string } {
  if (!round) return { threeUp: '', twoDown: '' }
  const wn = getWinningNumbers(round)
  const res = (
    round as {
      yeekee?: { result?: { threeDigitTop?: string; twoDigitDown?: string; twoDigitTop?: string } }
    }
  ).yeekee?.result
  return {
    threeUp: wn.threeUp || res?.threeDigitTop || '',
    twoDown: wn.twoDown || res?.twoDigitDown || res?.twoDigitTop || '',
  }
}

function CardShell({
  title,
  badge,
  imgSrc,
  onImgError,
  featured,
  variant,
  wipActive,
  empty,
  ready,
  children,
  className = '',
  animIndex = 0,
}: {
  title: string
  badge: string
  imgSrc: string
  onImgError: (e: React.SyntheticEvent<HTMLImageElement>) => void
  featured?: boolean
  variant?: 'gov' | 'yeekee'
  wipActive?: boolean
  /** ไม่มีรอบในวันนี้ — สไตล์ muted สื่อว่ายังไม่ออกงวด */
  empty?: boolean
  /** ออกผลแล้ว — การ์ดโทนสว่าง */
  ready?: boolean
  children: React.ReactNode
  className?: string
  animIndex?: number
}) {
  const variantClass = variant === 'gov' ? ' htdr-card--gov' : variant === 'yeekee' ? ' htdr-card--yeekee' : ''
  return (
    <article
      className={`htdr-card htdr-card--anim${featured ? ' htdr-card--featured' : ''}${variantClass}${wipActive ? ' is-wip' : ''}${empty ? ' htdr-card--empty' : ''}${ready && !empty ? ' htdr-card--ready' : ''}${className ? ` ${className}` : ''}`}
      style={{ ['--htdr-i' as string]: Math.min(animIndex, 14) }}
    >
      {wipActive ? <span className="htdr-card__wip-ring" aria-hidden /> : null}
      <header className="htdr-card__head">
        <div className="htdr-card__identity">
          <div className={`htdr-card__icon${wipActive ? ' wip_wup' : ''}`}>
            <img src={imgSrc} alt="" onError={onImgError} />
          </div>
          <div className="htdr-card__titles">
            {variant === 'gov' ? <span className="htdr-card__ribbon">หวยใหญ่</span> : null}
            <h3 className={`htdr-card__title${wipActive ? ' wip_wup' : ''}`} title={title}>
              {title}
            </h3>
          </div>
        </div>
        <span
          className={`htdr-card__badge${empty ? ' htdr-card__badge--empty' : ''}${ready && !empty ? ' htdr-card__badge--ready' : ''}${wipActive ? ' wip_wup' : ''}`}
        >
          {badge}
        </span>
      </header>
      <div className="htdr-card__body">{children}</div>
    </article>
  )
}

const HtdLotteryResults: React.FC<HtdLotteryResultsProps> = ({
  tenantId,
  getAssetUrl,
  handleImgError,
  showPoolError = true,
  wipWupMinutes = 15,
  wipWupMinutesOther = 15,
  selectedDate: selectedDateProp,
  onSelectedDateChange,
  showDatePicker = false,
  targetRoundId = null,
  onYeekeeRoundClick,
  className = '',
}) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [selectedDateState, setSelectedDateState] = useState(todayBangkokYYYYMMDD)
  const selectedDate = selectedDateProp ?? selectedDateState
  const setSelectedDate = (date: string) => {
    if (onSelectedDateChange) onSelectedDateChange(date)
    else setSelectedDateState(date)
  }
  const [tab, setTab] = useState<TabId>('all')
  const [animTick, setAnimTick] = useState(0)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(id)
  }, [])

  const switchTab = (id: TabId) => {
    setTab(id)
    setAnimTick((n) => n + 1)
  }

  useEffect(() => {
    const onHuayTypeUpdated = () => setRefreshTrigger((t) => t + 1)
    window.addEventListener('huay_type_updated', onHuayTypeUpdated)
    return () => window.removeEventListener('huay_type_updated', onHuayTypeUpdated)
  }, [])

  const {
    poolError,
    loading,
    latestGovernmentRound,
    latestYeekeeRound,
    recentYeekeeRounds,
    latestEgovRound,
    latestGsbRound,
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
  } = useLotteryResults(selectedDate, { refreshTrigger, targetRoundId }, tenantId)

  const gov = latestGovernmentRound
  const govWn = getWinningNumbers(gov)
  const govThreeDownList = getGovernmentThreeDownList(gov)
  const govHas = hasAnyResult(gov)
  const govCancelled = isRoundCancelled(gov)
  const dispGov = (v: string) => displayValue(v, { isCancelled: govCancelled, hasResult: govHas })

  const tabs = useMemo(() => {
    const list: { id: TabId; label: string; count?: number }[] = [{ id: 'all', label: 'ทั้งหมด' }]
    // Always show this tab so ธ.ก.ส. / ออมสิน stay visible even when no rounds today
    list.push({ id: 'gov', label: 'รัฐบาล / ยี่กี' })
    if (latestSetRounds.length) list.push({ id: 'set', label: 'เลขชุด', count: latestSetRounds.length })
    if (latestThaiStockRounds.length) list.push({ id: 'thai_stock', label: 'หุ้นไทย' })
    if (latestForeignRounds.length)
      list.push({ id: 'foreign', label: 'ต่างประเทศ', count: latestForeignRounds.length })
    if (latestForeignStockRounds.length)
      list.push({ id: 'stock', label: 'หุ้น', count: latestForeignStockRounds.length })
    if (latestVipRounds.length) list.push({ id: 'vip', label: 'VIP', count: latestVipRounds.length })
    if (latestVisaRounds.length) list.push({ id: 'visa', label: 'VISA', count: latestVisaRounds.length })
    if (allYeekeeVipRounds.length)
      list.push({ id: 'yeekee_vip', label: 'ยี่กี VIP', count: allYeekeeVipRounds.length })
    return list
  }, [
    latestSetRounds.length,
    latestThaiStockRounds.length,
    latestForeignRounds.length,
    latestForeignStockRounds.length,
    latestVipRounds.length,
    latestVisaRounds.length,
    allYeekeeVipRounds.length,
  ])

  useEffect(() => {
    if (!tabs.some((t) => t.id === tab)) setTab('all')
  }, [tabs, tab])

  const show = (id: TabId) => tab === 'all' || tab === id

  const roundWip = (round: LotteryRound | null | undefined) =>
    isRoundWipWup(round, getWipThreshold(round, wipWupMinutes, wipWupMinutesOther), now)

  const renderSimple3x2 = (
    round: LotteryRound | null,
    title: string,
    fallbackFlag: string,
    key?: string,
    animIndex = 0
  ) => {
    const hasRound = Boolean(round)
    const wn = getWinningNumbers(round)
    const hasResult = hasAnyResult(round)
    const cancelled = isRoundCancelled(round)
    const wip = roundWip(round)
    const disp = (v: string) => displayValue(v, { isCancelled: cancelled, hasResult })
    return (
      <CardShell
        key={key}
        title={title}
        badge={hasRound ? getRoundDrawLabel(round, selectedDate) : 'ยังไม่มีงวด'}
        imgSrc={getRoundImageSrc(round, getAssetUrl, fallbackFlag)}
        onImgError={handleImgError}
        animIndex={animIndex}
        wipActive={hasRound ? wip : false}
        empty={!hasRound}
        ready={hasRound && hasResult && !cancelled}
      >
        {!hasRound ? (
          <div className="htdr-empty-round" role="status">
            <span className="htdr-empty-round__label">ยังไม่มีรอบในวันนี้</span>
            <div className="htdr-digits htdr-digits--2">
              <DigitTile label="3 ตัวบน" value="—" />
              <DigitTile label="2 ตัวล่าง" value="—" />
            </div>
          </div>
        ) : cancelled ? (
          <p className="htdr-cancelled">ยกเลิก</p>
        ) : (
          <div className={`htdr-digits htdr-digits--2${wip ? ' is-wip-zone' : ''}`}>
            <DigitTile label="3 ตัวบน" value={disp(wn.threeUp)} wip={wip} />
            <DigitTile label="2 ตัวล่าง" value={disp(wn.twoDown)} wip={wip} />
          </div>
        )}
      </CardShell>
    )
  }

  return (
    <div className={`htdr${className ? ` ${className}` : ''}`}>
      {showPoolError && poolError ? <p className="htdr-error">{poolError}</p> : null}

      {showDatePicker ? (
        <div className="htdr-toolbar">
          <label className="htdr-date">
            <span className="htdr-date__label">
              <i className="far fa-calendar-alt" aria-hidden />
              เลือกวันที่
            </span>
            <input
              type="date"
              className="htdr-date__input"
              value={selectedDate}
              max={todayBangkokYYYYMMDD()}
              onChange={(e) => setSelectedDate(e.target.value)}
              aria-label="เลือกวันที่ดูผลหวย"
            />
          </label>
        </div>
      ) : null}

      {loading && !gov && !latestYeekeeRound ? (
        <div className="htdr-loading" aria-busy="true">
          <span className="htdr-loading__dot" />
          <span className="htdr-loading__dot" />
          <span className="htdr-loading__dot" />
          <span>กำลังโหลดผลรางวัล…</span>
        </div>
      ) : null}

      <div className="htdr-tabs" role="tablist" aria-label="หมวดผลรางวัล">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`htdr-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => switchTab(t.id)}
          >
            {t.label}
            {typeof t.count === 'number' ? <em>{t.count}</em> : null}
          </button>
        ))}
      </div>

      <div className="htdr-panels" key={`panel-${tab}-${animTick}`}>
      {/* Featured: รัฐบาลไทย + ยี่กี + ธ.ก.ส. + ออมสิน
          - การ์ดคงไว้เสมอเมื่อไม่มีรอบ: empty state (ไม่โชว์ รอผล ปลอม) */}
      {show('gov') ? (
        <section className="htdr-section" aria-label="หวยรัฐบาล">
          <div className="htdr-featured">
            <CardShell
              featured
              variant="gov"
              animIndex={0}
              wipActive={gov ? roundWip(gov) : false}
              empty={!gov}
              ready={Boolean(gov) && govHas && !govCancelled}
              title="หวยรัฐบาลไทย"
              badge={gov ? getRoundDrawLabel(gov, selectedDate) : 'ยังไม่มีงวด'}
              imgSrc={getRoundImageSrc(gov, getAssetUrl, '/img/flag/th.png')}
              onImgError={handleImgError}
            >
              {!gov ? (
                <div className="htdr-empty-round" role="status">
                  <span className="htdr-empty-round__label">ยังไม่มีรอบในวันนี้</span>
                  <DigitTile label="รางวัลที่ 1" value="—" size="lg" />
                  <div className="htdr-digits htdr-digits--2 htdr-gov-pair">
                    <DigitTile label="3 ตัวบน" value="—" />
                    <DigitTile label="2 ตัวล่าง" value="—" />
                  </div>
                  <div className="htdr-gov-bottom3">
                    <span className="htdr-gov-bottom3__label">3 ตัวล่าง</span>
                    <span className="htdr-gov-bottom3__pending">—</span>
                  </div>
                </div>
              ) : govCancelled ? (
                <p className="htdr-cancelled">ยกเลิก</p>
              ) : (
                <>
                  <DigitTile label="รางวัลที่ 1" value={dispGov(govWn.first)} size="lg" wip={roundWip(gov)} />
                  <div className={`htdr-digits htdr-digits--2 htdr-gov-pair${roundWip(gov) ? ' is-wip-zone' : ''}`}>
                    <DigitTile label="3 ตัวบน" value={dispGov(govWn.threeUp)} wip={roundWip(gov)} />
                    <DigitTile label="2 ตัวล่าง" value={dispGov(govWn.twoDown)} wip={roundWip(gov)} />
                  </div>
                  <GovBottomThreeBlock
                    numbers={govThreeDownList}
                    isCancelled={govCancelled}
                    hasResult={govHas}
                    wip={roundWip(gov)}
                  />
                </>
              )}
            </CardShell>

            {latestYeekeeRound
              ? (() => {
                  const yeekeeDigits = getYeekeeResultDigits(latestYeekeeRound)
                  const yeekeeHas =
                    hasAnyResult(latestYeekeeRound) || !!(yeekeeDigits.threeUp || yeekeeDigits.twoDown)
                  const yeekeeCancelled = isRoundCancelled(latestYeekeeRound)
                  const yeekeeWip = roundWip(latestYeekeeRound)
                  const disp = (v: string) =>
                    displayValue(v, { isCancelled: yeekeeCancelled, hasResult: yeekeeHas })
                  const roundLabel = formatYeekeeRoundLabel(latestYeekeeRound.roundNumber)
                  const timeStr = latestYeekeeRound.betEndTime
                    ? new Date(latestYeekeeRound.betEndTime).toLocaleTimeString('th-TH', {
                        timeZone: 'Asia/Bangkok',
                        hour: '2-digit',
                        minute: '2-digit',
                      }) + ' น.'
                    : '-'
                  const previousRounds = recentYeekeeRounds
                    .filter((r) => r._id !== latestYeekeeRound._id)
                    .slice(0, 6)
                  return (
                    <CardShell
                      featured
                      variant="yeekee"
                      animIndex={1}
                      wipActive={yeekeeWip}
                      ready={yeekeeHas && !yeekeeCancelled}
                      title="หวยยี่กี"
                      badge={getYeekeeRoundTimeLabel(latestYeekeeRound)}
                      imgSrc={getRoundImageSrc(latestYeekeeRound, getAssetUrl, '/img/flag/th.png')}
                      onImgError={handleImgError}
                    >
                      <div className="htdr-yeekee-meta">
                        <span className={yeekeeWip ? 'wip_wup' : undefined}>รอบที่ {roundLabel || '—'}</span>
                        <span className={yeekeeWip ? 'wip_wup' : undefined}>เวลา {timeStr}</span>
                      </div>
                      {yeekeeCancelled ? (
                        <p className="htdr-cancelled">ยกเลิก</p>
                      ) : (
                        <div className={`htdr-digits htdr-digits--2${yeekeeWip ? ' is-wip-zone' : ''}`}>
                          <DigitTile label="3 ตัวบน" value={disp(yeekeeDigits.threeUp)} size="lg" wip={yeekeeWip} />
                          <DigitTile
                            label="2 ตัวล่าง"
                            value={disp(yeekeeDigits.twoDown)}
                            size="lg"
                            wip={yeekeeWip}
                          />
                        </div>
                      )}

                      {previousRounds.length > 0 ? (
                        <div className="htdr-yeekee-recent">
                          <div className="htdr-yeekee-recent__head">
                            <strong>รอบก่อนหน้า</strong>
                            <span>{previousRounds.length} รอบ</span>
                          </div>
                          <div className="htdr-yeekee-recent__list">
                            {previousRounds.map((round) => {
                              const digits = getYeekeeResultDigits(round)
                              const cancelled = isRoundCancelled(round)
                              const hasResult = !!(digits.threeUp || digits.twoDown)
                              const threeUp = displayValue(digits.threeUp, {
                                isCancelled: cancelled,
                                hasResult,
                              })
                              const twoDown = displayValue(digits.twoDown, {
                                isCancelled: cancelled,
                                hasResult,
                              })
                              const rLabel = formatYeekeeRoundLabel(round.roundNumber)
                              const rTime = round.betEndTime
                                ? new Date(round.betEndTime).toLocaleTimeString('th-TH', {
                                    timeZone: 'Asia/Bangkok',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—'
                              const isClickable = !!onYeekeeRoundClick && round.status !== 'open'
                              const rowBody = (
                                <>
                                  <div className="htdr-yeekee-recent__round">
                                    <strong>รอบ {rLabel || '—'}</strong>
                                    <span>{rTime} น.</span>
                                  </div>
                                  <div className="htdr-yeekee-recent__nums">
                                    <span>
                                      <em>3 บน</em>
                                      {threeUp}
                                    </span>
                                    <span>
                                      <em>2 ล่าง</em>
                                      {twoDown}
                                    </span>
                                  </div>
                                </>
                              )
                              if (isClickable) {
                                return (
                                  <button
                                    key={round._id}
                                    type="button"
                                    className={`htdr-yeekee-recent__row is-clickable${hasResult && !cancelled ? ' is-ready' : ''}`}
                                    onClick={() => onYeekeeRoundClick?.(round)}
                                  >
                                    {rowBody}
                                  </button>
                                )
                              }
                              return (
                                <div
                                  key={round._id}
                                  className={`htdr-yeekee-recent__row${hasResult && !cancelled ? ' is-ready' : ''}`}
                                >
                                  {rowBody}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ) : null}
                    </CardShell>
                  )
                })()
              : null}
          </div>

          <h2 className="htdr-section__title">หวยธนาคาร (รัฐบาล)</h2>
          <div className="htdr-grid htdr-grid--2">
            {renderSimple3x2(
              latestEgovRound,
              (latestEgovRound?.huayName as string) || 'หวย ธ.ก.ส.',
              '/img/flag/egov.png',
              latestEgovRound?._id || 'egov',
              2
            )}
            {renderSimple3x2(
              latestGsbRound,
              (latestGsbRound?.huayName as string) || 'หวยออมสิน',
              '/img/flag/gsb.png',
              latestGsbRound?._id || 'gsb',
              3
            )}
          </div>
        </section>
      ) : null}

      {/* เลขชุด */}
      {show('set') && latestSetRounds.length > 0 ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวยเลขชุด</h2>
          <div className="htdr-grid htdr-grid--2">
            {latestSetRounds.slice(0, MAX_CARDS).map((round, i) => {
              const wn = getWinningNumbers(round)
              const hasResult = hasAnyResult(round)
              const cancelled = isRoundCancelled(round)
              const wip = roundWip(round)
              const disp = (v: string) => displayValue(v, { isCancelled: cancelled, hasResult })
              return (
                <CardShell
                  key={round._id}
                  animIndex={i}
                  wipActive={wip}
                  ready={hasResult && !cancelled}
                  title={(round.huayName as string) ?? 'หวยชุด'}
                  badge={getRoundDrawLabel(round, selectedDate)}
                  imgSrc={getRoundImageSrc(round, getAssetUrl, '/img/flag/th.png')}
                  onImgError={handleImgError}
                >
                  {cancelled ? (
                    <p className="htdr-cancelled">ยกเลิก</p>
                  ) : (
                    <DigitTile label="เลขชุด 4 ตัว" value={disp(wn.fourUp)} size="lg" wip={wip} />
                  )}
                </CardShell>
              )
            })}
          </div>
        </section>
      ) : null}

      {/* หุ้นไทย */}
      {show('thai_stock') && (latestThaiStockRounds[0] || tab === 'thai_stock') ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวยหุ้นไทย</h2>
          <div className="htdr-grid htdr-grid--2">
            {renderSimple3x2(
              latestThaiStockRounds[0] ?? null,
              (latestThaiStockRounds[0]?.huayName as string) ?? 'หวยหุ้นไทยปิดเย็น',
              '/img/flag/th.png',
              latestThaiStockRounds[0]?._id || 'thai-stock',
              0
            )}
          </div>
        </section>
      ) : null}

      {/* ต่างประเทศ */}
      {show('foreign') && latestForeignRounds.length > 0 ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวยต่างประเทศ</h2>
          <div className="htdr-grid htdr-grid--3">
            {latestForeignRounds
              .slice(0, MAX_CARDS)
              .map((round, i) =>
                renderSimple3x2(
                  round,
                  (round.huayName as string) ?? 'หวยต่างประเทศ',
                  '/img/flag/la.png',
                  round._id,
                  i
                )
              )}
          </div>
        </section>
      ) : null}

      {/* หุ้น */}
      {show('stock') && latestForeignStockRounds.length > 0 ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวยหุ้น</h2>
          <div className="htdr-grid htdr-grid--3">
            {latestForeignStockRounds
              .slice(0, MAX_CARDS)
              .map((round, i) =>
                renderSimple3x2(
                  round,
                  (round.huayName as string) ?? 'หวยหุ้น',
                  '/img/flag/la.png',
                  round._id,
                  i
                )
              )}
          </div>
        </section>
      ) : null}

      {/* VIP */}
      {show('vip') && latestVipRounds.length > 0 ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวย VIP</h2>
          <div className="htdr-grid htdr-grid--3">
            {latestVipRounds
              .slice(0, MAX_CARDS)
              .map((round, i) =>
                renderSimple3x2(
                  round,
                  (round.huayName as string) ?? 'หวย VIP',
                  '/img/flag/la.png',
                  round._id,
                  i
                )
              )}
          </div>
        </section>
      ) : null}

      {/* VISA */}
      {show('visa') && latestVisaRounds.length > 0 ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวย VISA</h2>
          <div className="htdr-grid htdr-grid--3">
            {latestVisaRounds
              .slice(0, MAX_CARDS)
              .map((round, i) =>
                renderSimple3x2(
                  round,
                  (round.huayName as string) ?? 'หวย VISA',
                  '/img/flag/la.png',
                  round._id,
                  i
                )
              )}
          </div>
        </section>
      ) : null}

      {/* ยี่กี VIP */}
      {show('yeekee_vip') && allYeekeeVipRounds.length > 0 ? (
        <section className="htdr-section">
          <h2 className="htdr-section__title">หวยยี่กี VIP</h2>
          <div className="htdr-yeekee-grid">
            {[...allYeekeeVipRounds]
              .sort(
                (a, b) => new Date(a.betStartTime).getTime() - new Date(b.betStartTime).getTime()
              )
              .map((round, yi) => {
                const wip = roundWip(round)
                const betStart = new Date(round.betStartTime).getTime()
                const betEnd = new Date(round.betEndTime).getTime()
                const isOpen = round.status === 'open' && round.isOpen !== false
                const isInBetWindow = isOpen && now >= betStart && now <= betEnd
                const isFuture = isOpen && now < betStart
                const isCancelled = round.status === 'cancelled'
                const isClosed = !isFuture && (!isOpen || now > betEnd) && !isCancelled
                const res = (
                  round as {
                    yeekee?: {
                      result?: {
                        threeDigitTop?: string
                        twoDigitDown?: string
                        twoDigitTop?: string
                      }
                    }
                  }
                ).yeekee?.result
                const threeUp = res?.threeDigitTop ?? ''
                const twoDown = res?.twoDigitDown ?? res?.twoDigitTop ?? ''
                const drawTime = new Date(round.betEndTime).toLocaleTimeString('th-TH', {
                  timeZone: 'Asia/Bangkok',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                const roundLabel = formatYeekeeRoundLabel(round.roundNumber)
                const displayThreeUp = isCancelled ? 'ยกเลิก' : isFuture ? 'รอผล' : threeUp || 'รอผล'
                const displayTwoDown = isCancelled ? 'ยกเลิก' : isFuture ? 'รอผล' : twoDown || 'รอผล'
                const hasResultOut = !isCancelled && !isFuture && !!(threeUp || twoDown)
                const isClickable = !!onYeekeeRoundClick && !isOpen && !isFuture
                return (
                  <article
                    key={round._id}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    className={`htdr-yk htdr-yk--anim${isCancelled ? ' is-cancelled' : ''}${isClosed ? ' is-closed' : ''}${wip ? ' is-wip' : ''}${isInBetWindow && !wip ? ' is-open' : ''}${hasResultOut ? ' is-ready' : ''}${isClickable ? ' is-clickable' : ''}`}
                    style={{ ['--htdr-i' as string]: Math.min(yi, 20) }}
                    onClick={isClickable ? () => onYeekeeRoundClick?.(round) : undefined}
                    onKeyDown={
                      isClickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onYeekeeRoundClick?.(round)
                            }
                          }
                        : undefined
                    }
                  >
                    {wip ? <span className="htdr-yk__wip-ring" aria-hidden /> : null}
                    <div className="htdr-yk__top">
                      <strong className={wip ? 'wip_wup' : undefined}>รอบ {roundLabel}</strong>
                      <span className={wip ? 'wip_wup' : undefined}>{drawTime} น.</span>
                    </div>
                    {isCancelled ? (
                      <p className="htdr-cancelled">ยกเลิก</p>
                    ) : (
                      <div className={`htdr-digits htdr-digits--2${wip ? ' is-wip-zone' : ''}`}>
                        <DigitTile label="3 บน" value={displayThreeUp} size="sm" wip={wip} />
                        <DigitTile label="2 ล่าง" value={displayTwoDown} size="sm" wip={wip} />
                      </div>
                    )}
                  </article>
                )
              })}
          </div>
        </section>
      ) : null}
      </div>
    </div>
  )
}

export default HtdLotteryResults
